import React, { useState } from 'react';
import { CheckCircle2, X, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QuickStatOverrideDialog } from '@/components/live-scoring/QuickStatOverrideDialog';
import type { MatchStatsResponse } from '@/api/feed/getFeedMatchStats';
import type { TeamScoringQuestionProps } from '@/utils/scoring/adapters/teamScoringAdapter';
import type { Option } from '@/api/questions/getQuestions';

type TeamScoringOptionType = TeamScoringQuestionProps['options'][number] & {
  third_image_en?: string | null;
  third_image_fr?: string | null;
  grading_data?: Option['grading_data'] | null;
};

interface TeamScoringOptionProps {
  questionId: string;
  fixtureId?: string;
  option: TeamScoringOptionType;
  statTypeCode?: string | null;
  language?: 'en' | 'fr';
  manualScoring?: boolean;
  matchStats?: MatchStatsResponse;
  onStatChange?: (optionId: string, statValue: number) => void;
  onSave?: (optionId: string) => void;
  onCancel?: (optionId: string) => void;
  onOverrideSuccess?: () => void;
}

export function TeamScoringOption({
  questionId,
  fixtureId: fixtureIdProp,
  option,
  statTypeCode,
  language = 'en',
  manualScoring = false,
  matchStats,
  onStatChange,
  onSave,
  onCancel,
  onOverrideSuccess,
}: TeamScoringOptionProps) {
  const optionText =
    language === 'en' ? option.option_text_en : option.option_text_fr || option.option_text_en;
  const optionImage =
    language === 'en' ? option.option_image_en : option.option_image_fr || option.option_image_en;
  const thirdImage =
    language === 'en' ? option.third_image_en : option.third_image_fr || option.third_image_en;

  const isCorrect = option.is_correct;
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState('');
  const [isOverrideDialogOpen, setIsOverrideDialogOpen] = useState(false);

  // Extract stat value for display
  let statValue: number | null = null;
  try {
    if (statTypeCode && option.stats) {
      if (Array.isArray(option.stats)) {
        const stat = option.stats.find(
          (s: { statType: string; value: number }) => s.statType === statTypeCode
        );
        statValue = stat?.value ?? null;
      } else if (typeof option.stats === 'object' && option.stats !== null) {
        const statsObj = option.stats as Record<string, unknown>;
        const value = statsObj[statTypeCode];
        statValue = typeof value === 'number' ? value : null;
      }
    }
  } catch (error) {
    console.error('Error extracting stat value:', error);
  }

  const displayValue = statValue !== null ? statValue.toString() : '0';

  // Extract teamId from grading_data
  const teamId = option.grading_data?.teamId?.toString();

  // Get fixtureId from matchStats or prop (prop takes precedence)
  const fixtureId = fixtureIdProp || matchStats?.match.id;

  const handleEditClick = () => {
    if (manualScoring) {
      // If we have fixtureId, teamId, and statTypeCode, use QuickStatOverrideDialog
      if (fixtureId && teamId && statTypeCode) {
        setIsOverrideDialogOpen(true);
      } else {
        // Fallback to inline editing if we don't have all required data
        setEditingValue(displayValue);
        setIsEditing(true);
      }
    }
  };

  const handleSave = () => {
    if (option.option_id && onStatChange) {
      const numValue = parseInt(editingValue, 10);
      if (!isNaN(numValue)) {
        onStatChange(option.option_id, numValue);
      }
    }
    if (option.option_id && onSave) {
      onSave(option.option_id);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (option.option_id && onCancel) {
      onCancel(option.option_id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="flex flex-col items-center gap-2 flex-1">
      {/* Option Card */}
      <div
        className={`relative w-full p-6 rounded-lg border transition-colors flex flex-col items-center justify-center min-h-[140px] ${
          isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}
      >
        {/* Checkmark Icon - Top Right */}
        {isCorrect && (
          <div className="absolute top-2 right-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
        )}

        {/* Option Image */}
        {(optionImage || thirdImage) && (
          <div className="mb-3">
            {optionImage && (
              <img src={optionImage} alt={optionText} className="h-16 w-16 object-contain" />
            )}
            {thirdImage && !optionImage && (
              <img src={thirdImage} alt="" className="h-16 w-16 object-contain" />
            )}
          </div>
        )}

        {/* Option Text */}
        <div className="text-center">
          <span className={`text-sm font-medium ${isCorrect ? 'text-gray-900' : 'text-gray-700'}`}>
            {optionText}
          </span>
        </div>
      </div>

      {/* Stat Value with Edit Controls - Center aligned */}
      <div className="flex items-center justify-center gap-2 w-full">
        {isEditing ? (
          <>
            <Input
              type="number"
              value={editingValue}
              onChange={e => setEditingValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-8 w-16 text-center"
              autoFocus
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleSave}
              className="h-7 px-2 text-xs bg-blue-600 hover:bg-blue-700"
            >
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-7 w-7 p-0 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <span
              className={`text-lg font-semibold ${isCorrect ? 'text-green-600' : 'text-gray-600'}`}
            >
              {displayValue}
            </span>
            {manualScoring && (
              <button
                type="button"
                onClick={handleEditClick}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label="Edit stat value"
              >
                <Pencil className="h-4 w-4 text-purple-600" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Quick Stat Override Dialog */}
      {fixtureId && teamId && statTypeCode && (
        <QuickStatOverrideDialog
          open={isOverrideDialogOpen}
          onOpenChange={setIsOverrideDialogOpen}
          questionId={questionId}
          fixtureId={fixtureId}
          teamId={teamId}
          statType={statTypeCode}
          currentValue={statValue ?? undefined}
          onSuccess={() => {
            setIsOverrideDialogOpen(false);
            onOverrideSuccess?.();
          }}
        />
      )}
    </div>
  );
}
